// pricing-calculator/hooks/useShareState.ts
// SSR-safe URL/localStorage sync for shareable calculator state, plus helpers.

import { useEffect, useMemo } from 'react';
import type {
  CalculatorSpec,
  CalculatorState,
  ShareableCalculatorState,
} from '@dopomogai/types';
import {
  buildShareUrl,
  copyShareUrl as copyShareUrlUtil,
  readShareStateFromUrl,
  updateBrowserUrlWithState,
} from '../utils/share';

type PersistMode = 'url' | 'localStorage' | 'none';

type Options = {
  persist?: PersistMode;                   // default: 'url'
  localStorageKey?: string;                // default: `pricing-calculator:${spec.title}`
  aliases?: any;                           // share param aliases; passed to utils/share (kept as any to avoid re-export)
  isEnabled?: boolean;                     // default: true
};

/**
 * Extract a minimal, serializable state snapshot for sharing/persisting.
 */
export function toShareableState(state: CalculatorState): ShareableCalculatorState {
  const roi: ShareableCalculatorState['roi'] = {};
  const { roiInputs } = state;

  // Only include defined numeric ROI fields
  (['sessions', 'cvrPct', 'aov', 'marginPct', 'ticketsMonthly', 'costPerTicket'] as const).forEach((k) => {
    const v = roiInputs[k];
    if (typeof v === 'number' && Number.isFinite(v)) {
      roi[k] = v;
    }
  });

  return {
    platformId: state.platformId,
    selectedModuleIds: Array.from(state.selectedModuleIds || []),
    presetId: state.presetId,
    roi: Object.keys(roi).length ? roi : undefined,
  };
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function getDefaultLSKey(spec: CalculatorSpec): string {
  return `pricing-calculator:${spec.title}`;
}

/**
 * Persist and share calculator state via URL or localStorage.
 * - Writes on changes (if enabled).
 * - Exposes helpers to build/copy a share URL.
 */
export function useShareState(
  spec: CalculatorSpec,
  state: CalculatorState,
  options?: Options
): {
  buildShareUrlFromState: () => string;
  copyShareUrlFromState: () => Promise<string>;
  readPrefillFromUrl: () => ShareableCalculatorState;
  readPrefillFromLocalStorage: () => ShareableCalculatorState | undefined;
  persistSnapshotToLocalStorage: () => void;
} {
  const { persist = 'url', aliases, isEnabled = true } = options || {};
  const localStorageKey = options?.localStorageKey || getDefaultLSKey(spec);

  const shareable = useMemo(() => toShareableState(state), [state]);

  // Write on changes
  useEffect(() => {
    if (!isEnabled) return;
    if (persist === 'url') {
      updateBrowserUrlWithState(shareable, aliases);
    } else if (persist === 'localStorage') {
      if (!isBrowser()) return;
      try {
        window.localStorage.setItem(localStorageKey, JSON.stringify(shareable));
      } catch {
        // ignore quota/SSR errors
      }
    }
  }, [shareable, persist, aliases, isEnabled, localStorageKey]);

  function readPrefillFromUrl(): ShareableCalculatorState {
    return readShareStateFromUrl(undefined, aliases);
  }

  function readPrefillFromLocalStorage(): ShareableCalculatorState | undefined {
    if (!isBrowser()) return undefined;
    try {
      const raw = window.localStorage.getItem(localStorageKey);
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as ShareableCalculatorState;
      // Basic validation
      return parsed && typeof parsed === 'object' ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  function persistSnapshotToLocalStorage(): void {
    if (!isBrowser()) return;
    try {
      window.localStorage.setItem(localStorageKey, JSON.stringify(shareable));
    } catch {
      // ignore
    }
  }

  function buildShareUrlFromState(): string {
    return buildShareUrl(shareable, { aliases, replaceExisting: true });
  }

  async function copyShareUrlFromState(): Promise<string> {
    return copyShareUrlUtil(shareable, { aliases, replaceExisting: true });
  }

  return {
    buildShareUrlFromState,
    copyShareUrlFromState,
    readPrefillFromUrl,
    readPrefillFromLocalStorage,
    persistSnapshotToLocalStorage,
  };
}
